import { useParams, useNavigate } from 'react-router';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useJob } from '../hooks/useJob';
import { JobCostOverview } from '../components/JobCostOverview';

/**
 * Full-screen Job Cost page rendered at /jobs/:id/cost.
 *
 * Thin wrapper around {@link JobCostOverview} that resolves the job id from
 * the URL and loads the job detail (JobCostOverview requires the full
 * JobDetailDto). Kept minimal — structural/UX refactors (header labels, tab
 * removal, link updates) happen in later phases of the plan.
 */
export function JobCostPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobId = id ? parseInt(id, 10) : undefined;

  const { data: job, isLoading, error } = useJob(jobId);
  usePageTitle(job ? `Job #${job.jobNumber} - ${job.lead.customerName}` : 'Job Cost');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" label="Loading job..." />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="container mx-auto p-6">
        <ErrorAlert error={error || 'Job not found'} className="rounded-lg p-4" />
        <button
          onClick={() => navigate('/jobs')}
          className="mt-4 text-secondary hover:underline"
        >
          ← Back to Jobs
        </button>
      </div>
    );
  }

  return <JobCostOverview jobId={job.id} job={job} />;
}
